import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  Alert,
  Snackbar,
  Typography,
} from '@mui/material';
import { Add, Search } from '@mui/icons-material';
import useDebounce from '../../../hooks/useDebounce';
import PageHeader from '../../../components/PageHeader';
import CompanyTable from '../components/CompanyTable';
import CompanyModalForm from '../components/CompanyModalForm';
import LoadingLogo from '../../../components/LoadingLogo';
import companyService from '../services/companyService';
import { usePermissions } from '../../../hooks/usePermissions';

const CompanyList = () => {
  const { hasPermission } = usePermissions();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 2000);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadCompanies();
  }, [page, debouncedSearch]);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const response = await companyService.getAll({ search: debouncedSearch, page, limit: 10 });
      setCompanies(response.data);
      setPagination(response.pagination);
    } catch (error) {
      showSnackbar('Error al cargar empresas', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedCompany(null);
    setOpenModal(true);
  };

  const handleEdit = (company) => {
    setSelectedCompany(company);
    setOpenModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estas seguro de eliminar esta empresa?')) {
      try {
        await companyService.delete(id);
        setCompanies(prev => prev.filter(c => c.id !== id));
        setPagination(prev => prev ? { ...prev, total: (prev.total || 1) - 1 } : prev);
        showSnackbar('Empresa eliminada exitosamente', 'success');
      } catch (error) {
        showSnackbar('Error al eliminar empresa', 'error');
      }
    }
  };

  const handleSave = async (data) => {
    try {
      if (selectedCompany) {
        await companyService.update(selectedCompany.id, data);
        setCompanies(prev => prev.map(c =>
          c.id === selectedCompany.id ? { ...c, ...data } : c
        ));
        showSnackbar('Empresa actualizada exitosamente', 'success');
      } else {
        await companyService.create(data);
        showSnackbar('Empresa creada exitosamente', 'success');
        if (page !== 1) setPage(1);
        else loadCompanies();
      }
      setOpenModal(false);
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error al guardar empresa', 'error');
      throw error;
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  return (
    <Box>
      <PageHeader
        title="Empresas"
        subtitle="Listado de empresas cliente. Solo visible para el rol Owner."
        stat={pagination?.total || 0}
        statLabel="Empresas"
        actions={hasPermission('company.create') && (
          <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
            Nueva Empresa
          </Button>
        )}
      />

      <Box sx={{ bgcolor: '#f2f2f3', borderRadius: 0, p: '18px', border: '1px solid rgba(29,31,32,0.16)', mb: 3 }}>
        <Typography sx={{ fontSize: '0.75rem', color: 'rgba(29,31,32,0.55)', mb: 0.5, fontWeight: 500 }}>Buscar</Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Buscar por nombre, identificador o email..."
          value={search}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <LoadingLogo size={120} />
        </Box>
      ) : (
        <CompanyTable
          companies={companies}
          onEdit={handleEdit}
          onDelete={handleDelete}
          page={page}
          onPageChange={setPage}
          pagination={pagination}
          canEdit={hasPermission('company.update')}
          canDelete={hasPermission('company.delete')}
        />
      )}

      <CompanyModalForm
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSave={handleSave}
        company={selectedCompany}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompanyList;
